<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import SyncSettings from '$lib/components/drawers/client/integrations/SyncSettings.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {Eye, EyeSlash, Info} from 'phosphor-svelte';
    import {getModalStore} from '@skeletonlabs/skeleton';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {extractErrorMessage} from '$lib/utils/error/errorUtils';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  const modalStore = getModalStore();

  type Entity = {
    id: number;
    name: string;
    code: string;
    hasMyobAcumaticaConfig: boolean;
  };

  let entities: Entity[] = [];
  let selectedEntity: Entity | null = null;
  let newEntityName = '';
  let isLoadingEntities = false;
  let isLoading = false;
  let isExecuting = false;
  let isForcingSync = false;
  let hasExistingConfig = false;
  let showConfigSection = false;
  let showSecret = false;
  let urlError = '';

  let baseUrl = '';
  let tenantName = '';
  let branch = '';
  let ledger = '';
  let customEndpointName = '';
  let oAuthClientId = '';
  let oAuthClientSecret = '';
  let tokenExpiryUtc: string | null = null;

  let generatedUrl = '';
  let isGenerating = false;

  let isSavingSyncSettings = false;

  function defaultSyncSettings(): Record<string, boolean> {
    return {
      canSync: true,
      canInvoicePost: true,
      canExpenseClaimPost: false,
      syncTaxCodes: true,
      syncSuppliers: true,
      syncGeneralLedgerCodes: false,
      syncBudgets: true,
      syncSpends: true,
    };
  }

  const customSyncSettings = [
    { key: 'syncUsers', label: 'Users' },
    { key: 'syncSubAllocations', label: 'SubAllocations' },
    { key: 'syncBusinessUnits', label: 'Business Units' },
  ];

  function defaultCustomSyncSettings(): Record<string, boolean> {
    return {
      syncUsers: true,
      syncSubAllocations: true,
      syncBusinessUnits: true,
    };
  }

  let syncSettings: Record<string, boolean> = { ...defaultSyncSettings(), ...defaultCustomSyncSettings() };
  let lastConfirmedSyncSettings: Record<string, boolean> = { ...syncSettings };
  let loadConfigRequestId = 0;

  function handleApiError(error: unknown, message: string) {
    if (error instanceof Error) {
      console.error(`${message}:`, error.message);
      showToast({ title: 'Error', message: error.message, type: 'error' });
    } else {
      console.error(`${message}:`, error);
      showToast({ title: 'Error', message, type: 'error' });
    }
  }

  function validateBaseUrl(url: string): boolean {
    if (!url) {
      urlError = '';
      return false;
    }

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== 'https:') {
        urlError = 'URL must use https://';
        return false;
      }
    } catch {
      urlError = 'Enter a valid URL';
      return false;
    }

    urlError = '';
    return true;
  }

  $: isUrlValid = validateBaseUrl(baseUrl);
  $: isTenantNameValid = tenantName.trim().length > 0;
  $: isBranchValid = branch.trim().length > 0;
  $: isLedgerValid = ledger.trim().length > 0;
  $: secretIsHidden = oAuthClientSecret === '••••••';
  $: formIsValid = !!(clientId && baseUrl && isUrlValid && isTenantNameValid && isBranchValid && isLedgerValid && oAuthClientId.trim() && oAuthClientSecret);
  $: canSave = formIsValid && !isExecuting;
  $: showConfigSection = !!(selectedEntity || newEntityName);
  $: canGenerate = !!(clientId && entityId && hasExistingConfig);
  $: tokenIsExpired = !!(tokenExpiryUtc && new Date(tokenExpiryUtc) < new Date());
  $: tokenExpiryLabel = tokenExpiryUtc ? new Date(tokenExpiryUtc).toLocaleString() : '';
  $: tokenExpiredAgoLabel = tokenIsExpired ? formatRelativeTime(new Date(tokenExpiryUtc!)) : '';

  function formatRelativeTime(date: Date): string {
    const minutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  $: hasConfiguredEntities = entities.some((entity) => entity.hasMyobAcumaticaConfig);

  onMount(async () => {
    if (clientId) await loadInitialEntities();
  });

  async function loadInitialEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          if (entityId) {
            const existing = entities.find((entity) => entity.id.toString() === entityId);
            if (existing) {
              selectedEntity = existing;
              await loadExistingConfig();
            } else {
              entityId = '';
              resetForm();
            }
          }
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadExistingConfig() {
    if (!clientId || !entityId) return;
    const requestId = ++loadConfigRequestId;
    const requestedEntityId = entityId;
    isLoading = true;
    hasExistingConfig = false;
    generatedUrl = '';
    tokenExpiryUtc = null;
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/config/${clientId}/${requestedEntityId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      // A newer loadExistingConfig call (e.g. the user switched entities again) has since
      // started — discard this now-stale response instead of applying it to the wrong entity.
      if (requestId !== loadConfigRequestId) return;
      if (response.ok) {
        const result = await response.json();
        if (result.exists) {
          hasExistingConfig = true;
          const config = result.config;
          baseUrl = config.baseUrl ?? '';
          tenantName = config.tenantName ?? '';
          branch = config.branch ?? '';
          ledger = config.ledger ?? '';
          customEndpointName = config.customEndpointName ?? '';
          oAuthClientId = config.oAuthClientId ?? '';
          oAuthClientSecret = config.oAuthClientSecret ?? '';
          tokenExpiryUtc = config.tokenExpiryUtc ?? null;
          syncSettings = {
            canSync: config.canSync ?? true,
            canInvoicePost: config.canInvoicePost ?? true,
            canExpenseClaimPost: config.canExpenseClaimPost ?? false,
            syncTaxCodes: config.syncTaxCodes ?? true,
            syncSuppliers: config.syncSuppliers ?? true,
            syncGeneralLedgerCodes: config.syncGeneralLedgerCodes ?? false,
            syncBudgets: config.syncBudgets ?? true,
            syncSpends: config.syncSpends ?? true,
            syncUsers: config.syncUsers ?? true,
            syncSubAllocations: config.syncSubAllocations ?? true,
            syncBusinessUnits: config.syncBusinessUnits ?? true,
          };
          lastConfirmedSyncSettings = { ...syncSettings };
          showToast({ title: 'Existing Configuration Loaded', message: 'This entity has existing MYOB Acumatica configuration.', type: 'info' });
        }
      }
    } catch (error) {
      if (requestId === loadConfigRequestId) console.error('Failed to load MYOB Acumatica config:', error);
    } finally {
      if (requestId === loadConfigRequestId) isLoading = false;
    }
  }

  async function saveConfiguration() {
    if (!canSave) return;
    isExecuting = true;
    try {
      const response = await fetchWithAuth('/api/myobacumatica/setup', {
        method: 'POST',
        body: JSON.stringify({
          clientId: parseInt(clientId),
          entityId: entityId ? parseInt(entityId) : 0,
          entityName: newEntityName || selectedEntity?.name || clientName,
          baseUrl,
          tenantName,
          branch: branch || null,
          ledger: ledger || null,
          customEndpointName: customEndpointName || null,
          oAuthClientId,
          oAuthClientSecret,
        }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, `Failed to save MYOB Acumatica configuration (HTTP ${response.status})`);
        throw new Error(message);
      }

      const result = await response.json();
      const newEntityId = result.entityId;
      hasExistingConfig = true;
      showToast({ title: 'Success', message: 'MYOB Acumatica configuration saved successfully', type: 'success' });
      if (!entityId && newEntityId) {
        entityId = newEntityId.toString();
        await loadEntitiesWithoutResetting();
        const created = entities.find((entity) => entity.id === newEntityId);
        if (created) {
          newEntityName = '';
          selectedEntity = created;
        }
      } else {
        await loadEntitiesWithoutResetting();
      }
    } catch (error) {
      handleApiError(error, 'Failed to save MYOB Acumatica configuration');
    } finally {
      isExecuting = false;
    }
  }

  async function saveSyncSettings() {
    if (!clientId || !entityId || !hasExistingConfig) return;
    const attemptedSettings = { ...syncSettings };
    const savedForEntityId = entityId;
    isSavingSyncSettings = true;
    try {
      const response = await fetchWithAuth('/api/myobacumatica/sync-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: parseInt(clientId),
          entityId: parseInt(savedForEntityId),
          ...attemptedSettings,
        }),
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, `Failed to save sync settings (HTTP ${response.status})`);
        throw new Error(message);
      }

      lastConfirmedSyncSettings = attemptedSettings;
      showToast({ title: 'Success', message: 'Sync settings saved', type: 'success' });
    } catch (error) {
      // Revert the checkboxes to the last persisted state so the panel never shows settings
      // that failed to save as if they were active — but only if the user hasn't since
      // switched to a different entity, whose settings this revert shouldn't touch.
      if (entityId === savedForEntityId) {
        syncSettings = { ...lastConfirmedSyncSettings };
      }
      handleApiError(error, 'Failed to save sync settings');
    } finally {
      isSavingSyncSettings = false;
    }
  }

  async function generateUrl() {
    if (!canGenerate) return;

    isGenerating = true;
    generatedUrl = '';

    try {
      const response = await fetchWithAuth('/api/myobacumatica/generate-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: parseInt(clientId),
          entityId: parseInt(entityId),
        }),
      });

      const result = await response.json();

      if (result.success) {
        generatedUrl = result.url;
        showToast({ title: 'Success', message: 'Authentication link generated successfully', type: 'success' });
      } else {
        showToast({ title: 'Error', message: result.error || 'Failed to generate authentication link', type: 'error' });
      }
    } catch (error) {
      handleApiError(error, 'Failed to generate authentication link');
    } finally {
      isGenerating = false;
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok
          ? { title: 'Success', message: 'MYOB Acumatica sync has been queued', type: 'success' }
          : { title: 'Error', message: 'Failed to queue MYOB Acumatica sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue MYOB Acumatica sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }

  function copyToClipboard() {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl).then(() => {
      showToast({ title: 'Copied', message: 'Link copied to clipboard', type: 'success' });
    });
  }

  async function disconnect() {
    if (!clientId || !entityId || !hasExistingConfig || isExecuting) return;
    const confirmed = await confirmDisconnect(selectedEntity?.name ?? clientName);
    if (!confirmed) return;

    isExecuting = true;
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/disconnect/${clientId}/${entityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response, `Failed to disconnect MYOB Acumatica integration (HTTP ${response.status})`);
        throw new Error(message);
      }

      showToast({ title: 'Success', message: 'MYOB Acumatica integration disconnected successfully', type: 'success' });
      resetAll();
      await loadEntitiesWithoutResetting();
    } catch (error) {
      handleApiError(error, 'Failed to disconnect MYOB Acumatica integration');
    } finally {
      isExecuting = false;
    }
  }

  function confirmDisconnect(entityName: string): Promise<boolean> {
    return new Promise((resolve) => {
      modalStore.trigger({
        type: 'confirm',
        title: 'Disconnect MYOB Acumatica integration?',
        body: `This will disconnect the MYOB Acumatica integration for "${entityName}". You can configure it again later.`,
        buttonTextCancel: 'Cancel',
        buttonTextConfirm: 'Disconnect',
        modalClasses: '!max-w-md',
        response: resolve,
      });
    });
  }

  function resetForm() {
    baseUrl = '';
    tenantName = '';
    branch = '';
    ledger = '';
    customEndpointName = '';
    oAuthClientId = '';
    oAuthClientSecret = '';
    hasExistingConfig = false;
    showSecret = false;
    generatedUrl = '';
    tokenExpiryUtc = null;
    syncSettings = { ...defaultSyncSettings(), ...defaultCustomSyncSettings() };
    lastConfirmedSyncSettings = { ...syncSettings };
  }

  function resetAll() {
    resetForm();
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
  }

  async function loadEntitiesWithoutResetting() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          const currentEntityId = selectedEntity?.id;
          entities = result.entities;
          if (currentEntityId) {
            const updated = entities.find((entity) => entity.id === currentEntityId);
            if (updated) selectedEntity = updated;
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh entities:', error);
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    resetForm();
    try {
      const response = await fetchWithAuth(`/api/myobacumatica/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          showToast({ title: 'Success', message: 'Entities refreshed successfully', type: 'success' });
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  function handleEntitySelection() {
    if (selectedEntity) {
      newEntityName = '';
      entityId = selectedEntity.id.toString();
      if (selectedEntity.hasMyobAcumaticaConfig) {
        loadExistingConfig();
      } else {
        resetForm();
      }
    } else {
      entityId = '';
    }
  }

  function handleNewEntityInput() {
    if (newEntityName && (selectedEntity !== null || hasExistingConfig)) {
      selectedEntity = null;
      entityId = '';
      resetForm();
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">MYOB Acumatica Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure MYOB Acumatica integration for this client using the client's Connected Application credentials.</p>

  <div class="mb-4 flex justify-end gap-2">
    <Button
      label={isForcingSync ? 'Queueing...' : 'Force sync'}
      onClick={forceSync}
      disabled={isLoadingEntities || isExecuting || isForcingSync || !hasConfiguredEntities}
      size="small"
    />
  </div>

  <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select or Create Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose an existing entity or create a new one for MYOB Acumatica integration.</p>

    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <label for="myobacumatica-entity-select" class="mb-2 block text-sm font-medium text-gray-700">Existing Entity</label>
        <div class="flex items-end gap-3">
          <select
            id="myobacumatica-entity-select"
            bind:value={selectedEntity}
            on:change={handleEntitySelection}
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={isLoadingEntities || newEntityName !== '' || isExecuting}
          >
            <option value={null}>-- Select Entity --</option>
            {#each entities as entity}
              <option value={entity}>{entity.name}{entity.hasMyobAcumaticaConfig ? ' (has MYOB Acumatica config)' : ''}</option>
            {/each}
          </select>
          <Button
            label={isLoadingEntities ? 'Loading...' : 'Refresh'}
            onClick={loadEntities}
            disabled={isLoadingEntities || isExecuting || isForcingSync}
            colour="secondary"
            size="small"
          />
        </div>
        {#if isLoadingEntities}<p class="mt-2 text-sm text-gray-500">Loading entities...</p>{/if}
      </div>

      <div>
        <label for="myobacumatica-new-entity-name" class="mb-2 block text-sm font-medium text-gray-700">New Entity Name</label>
        <input
          id="myobacumatica-new-entity-name"
          type="text"
          bind:value={newEntityName}
          on:input={handleNewEntityInput}
          placeholder="Enter new entity name"
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
          disabled={selectedEntity !== null || isExecuting}
        />
        <p class="mt-2 text-xs text-gray-500">Leave blank to select an existing entity</p>
      </div>
    </div>
  </div>

  {#if showConfigSection}
    <div class="mb-6 space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold">MYOB Acumatica Connected Application</h4>
        {#if isLoading}
          <span class="text-sm text-gray-500">Loading existing configuration...</span>
        {:else if hasExistingConfig}
          <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
            <Info size={16} />
            <span>Updating existing configuration</span>
          </div>
        {/if}
      </div>

      {#if newEntityName}
        <div class="rounded-md bg-green-50 p-3 text-sm text-green-700"><p>Creating new entity: <strong>{newEntityName}</strong></p></div>
      {/if}

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="myobacumatica-base-url" class="mb-2 flex items-center gap-2 text-sm font-medium">Base URL <RequiredBadge /></label>
          <input
            id="myobacumatica-base-url"
            type="url"
            bind:value={baseUrl}
            placeholder="https://example-tenant.myobadvanced.com/"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2"
            class:border-gray-300={!baseUrl || isUrlValid}
            class:border-error-500={baseUrl && !isUrlValid}
            disabled={isExecuting}
          />
          {#if baseUrl && urlError}<p class="mt-1 text-xs text-error-500">{urlError}</p>{/if}
        </div>

        <div>
          <label for="myobacumatica-tenant-name" class="mb-2 flex items-center gap-2 text-sm font-medium">Tenant Name <RequiredBadge /></label>
          <input
            id="myobacumatica-tenant-name"
            type="text"
            bind:value={tenantName}
            placeholder="Enter tenant name"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={isTenantNameValid}
            class:border-error-500={tenantName && !isTenantNameValid}
            class:border-primary-200={!tenantName}
            disabled={isExecuting}
          />
          {#if tenantName && !isTenantNameValid}<p class="mt-1 text-xs text-error-500">Tenant name is required</p>{/if}
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <div>
          <label for="myobacumatica-branch" class="mb-2 flex items-center gap-2 text-sm font-medium">Branch <RequiredBadge /></label>
          <input
            id="myobacumatica-branch"
            type="text"
            bind:value={branch}
            placeholder="Enter branch"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={isBranchValid}
            class:border-error-500={branch && !isBranchValid}
            class:border-primary-200={!branch}
            disabled={isExecuting}
          />
          {#if branch && !isBranchValid}<p class="mt-1 text-xs text-error-500">Branch is required</p>{/if}
        </div>
        <div>
          <label for="myobacumatica-custom-endpoint-name" class="mb-2 block text-sm font-medium">Custom Endpoint Name</label>
          <input
            id="myobacumatica-custom-endpoint-name"
            type="text"
            bind:value={customEndpointName}
            placeholder="Enter custom endpoint name (optional)"
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isExecuting}
          />
          <div class="pt-1.5">
            <FieldHint text="Optional. Only set this if the client's Acumatica admin has configured a separate, client-specific endpoint (e.g. named after the client) for Subaccount segment descriptions — leave blank for every other client." />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="myobacumatica-ledger" class="mb-2 flex items-center gap-2 text-sm font-medium">Ledger <RequiredBadge /></label>
          <input
            id="myobacumatica-ledger"
            type="text"
            bind:value={ledger}
            placeholder="Enter ledger"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={isLedgerValid}
            class:border-error-500={ledger && !isLedgerValid}
            class:border-primary-200={!ledger}
            disabled={isExecuting}
          />
          {#if ledger && !isLedgerValid}<p class="mt-1 text-xs text-error-500">Ledger is required</p>{/if}
          <div class="pt-1.5">
            <FieldHint text="The Ledger used for Budget/Spend sync — can differ per branch even within the same tenant (e.g. BUDGET2 for one branch, BUDGET for another)." />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="myobacumatica-oauth-client-id" class="mb-2 flex items-center gap-2 text-sm font-medium">OAuth Client ID <RequiredBadge /></label>
          <input
            id="myobacumatica-oauth-client-id"
            type="text"
            bind:value={oAuthClientId}
            placeholder="Enter OAuth client ID"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={oAuthClientId}
            class:border-primary-200={!oAuthClientId}
            disabled={isExecuting}
          />
        </div>

        <div>
          <label for="myobacumatica-oauth-client-secret" class="mb-2 flex items-center gap-2 text-sm font-medium">OAuth Client Secret <RequiredBadge /></label>
          {#if secretIsHidden}
            <div class="flex items-center gap-2">
              <div class="flex-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm italic text-gray-500">Secret is set but not visible</div>
              <Button
                label="Change"
                onClick={() => {
                  oAuthClientSecret = '';
                  showSecret = false;
                }}
                disabled={isExecuting}
                colour="secondary"
                size="small"
              />
            </div>
          {:else}
            <div class="relative">
              {#if showSecret}
                <input
                  id="myobacumatica-oauth-client-secret"
                  type="text"
                  bind:value={oAuthClientSecret}
                  placeholder="Enter OAuth client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={oAuthClientSecret}
                  class:border-primary-200={!oAuthClientSecret}
                  disabled={isExecuting}
                />
              {:else}
                <input
                  id="myobacumatica-oauth-client-secret"
                  type="password"
                  bind:value={oAuthClientSecret}
                  placeholder="Enter OAuth client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={oAuthClientSecret}
                  class:border-primary-200={!oAuthClientSecret}
                  disabled={isExecuting}
                />
              {/if}
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                on:click={() => (showSecret = !showSecret)}
                disabled={isExecuting}
              >
                {#if showSecret}<EyeSlash size={20} />{:else}<Eye size={20} />{/if}
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>

    <div class="mb-6 flex justify-end gap-3">
      {#if selectedEntity && hasExistingConfig}
        <Button label="Disconnect" onClick={disconnect} disabled={isExecuting} colour="error" />
      {/if}
      <Button
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
        onClick={saveConfiguration}
        disabled={!canSave}
        colour="primary"
      />
    </div>
  {/if}

  {#if hasExistingConfig}
    <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <SyncSettings settings={syncSettings} customSettings={customSyncSettings} isSaving={isSavingSyncSettings} onChange={saveSyncSettings} />
    </div>

    <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h4 class="mb-2 font-semibold">Authentication Link</h4>
      <p class="mb-4 text-sm text-gray-600">
        Once the Connected Application credentials above are saved, generate a link and send it to the client so they can
        log into MYOB Acumatica and approve the requested access.
      </p>

      {#if tokenExpiryUtc}
        {#if tokenIsExpired}
          <div class="mb-4 flex items-start gap-2 rounded-lg border border-error-300 bg-error-50 p-3 text-sm text-error-700">
            <span>⚠</span>
            <span>
              Token expired {tokenExpiredAgoLabel} (at {tokenExpiryLabel}) — click "Generate Authentication Link" below to
              reconnect.
            </span>
          </div>
        {:else}
          <div class="mb-4 flex items-start gap-2 rounded-lg border border-success-300 bg-success-50 p-3 text-sm text-success-700">
            <span>✓</span>
            <span>Connected — token valid until {tokenExpiryLabel}.</span>
          </div>
        {/if}
      {/if}

      <Button
        label={isGenerating ? 'Generating...' : 'Generate Authentication Link'}
        onClick={generateUrl}
        colour="primary"
        disabled={!canGenerate || isGenerating}
      />

      {#if generatedUrl}
        <div class="mt-4 space-y-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
          <div class="break-all rounded border border-gray-300 bg-white p-3 font-mono text-sm">
            {generatedUrl}
          </div>
          <div class="flex gap-2">
            <Button label="Copy to Clipboard" onClick={copyToClipboard} colour="secondary" size="small" />
            <a href={generatedUrl} target="_blank" rel="noopener noreferrer" class="btn variant-ghost-primary btn-sm">
              Open in New Tab
            </a>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>
